import React, { useRef, useEffect, useCallback, useMemo } from 'react';
import { ContentCard as ContentCardType, PersonSummaryWithPhoto } from '../types';
import PeoplePhotosOverlay from './PeoplePhotosOverlay';
import { useContentCardOptimization } from '../utils/contentCardOptimization';
import { getConfig } from '../config';

interface ContentCardProps {
  content: ContentCardType;
  onClick?: (content: ContentCardType) => void;
  className?: string;
  showPopularityScore?: boolean;
  people?: PersonSummaryWithPhoto[]; // Legacy prop for backward compatibility
  onPersonClick?: (person: PersonSummaryWithPhoto) => void;
  enableOptimizations?: boolean;
  priority?: 'high' | 'normal' | 'low';
}

const ContentCard: React.FC<ContentCardProps> = ({
  content,
  onClick,
  className = '',
  showPopularityScore = false,
  people,
  onPersonClick,
  enableOptimizations = true,
  priority = 'normal'
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const config = getConfig();
  
  const {
    observeCard,
    unobserveCard,
    preloadCardPeoplePhotos,
    startRender,
    endRender,
    cleanup
  } = useContentCardOptimization({
    enablePeoplePhotoPreloading: enableOptimizations && config.enableLazyLoading,
    maxPreloadedCards: priority === 'high' ? 15 : 10,
    preloadThreshold: priority === 'high' ? 300 : 200,
  });

  // Performance monitoring
  useEffect(() => {
    if (enableOptimizations) {
      startRender(content.id);
      return () => endRender(content.id);
    }
  }, [content.id, enableOptimizations, startRender, endRender]);

  // Observe card for performance optimizations
  useEffect(() => {
    if (enableOptimizations && cardRef.current) {
      observeCard(cardRef.current, content.id);
      
      // Preload people photos if available
      if (content.people) {
        preloadCardPeoplePhotos(content);
      }

      return () => {
        if (cardRef.current) {
          unobserveCard(cardRef.current);
        }
      };
    }
  }, [content.id, content.people, enableOptimizations, observeCard, unobserveCard, preloadCardPeoplePhotos]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (enableOptimizations) {
        cleanup();
      }
    };
  }, [enableOptimizations, cleanup]);

  const handleClick = useCallback(() => {
    if (onClick) {
      onClick(content);
    }
  }, [onClick, content]);

  const handleImageError = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    e.currentTarget.src = '/images/content-placeholder.svg'; // Better fallback
  }, []);

  // Use people data from content object if available, otherwise fall back to legacy people prop
  const displayPeople = useMemo(() => {
    return content.people 
      ? [...content.people.cast, ...content.people.directors, ...content.people.writers]
      : people;
  }, [content.people, people]);

  return (
    <div
      ref={cardRef}
      className={`group cursor-pointer transition-transform duration-300 hover:scale-105 ${className}`}
      onClick={handleClick}
      style={enableOptimizations ? {
        // Optimize for GPU acceleration
        willChange: 'transform',
        transform: 'translateZ(0)',
      } : undefined}
    >
      <div className="relative overflow-hidden rounded-lg bg-gray-800 py-4">
        <img
          src={content.imageUrl}
          alt={content.title}
          className="w-full h-56 sm:h-64 md:h-72 object-contain bg-gray-800 rounded-lg transition-opacity duration-300 group-hover:opacity-75"
          onError={handleImageError}
          loading={priority === 'high' ? 'eager' : 'lazy'}
          {...(enableOptimizations && {
            decoding: 'async',
            fetchpriority: priority === 'high' ? 'high' : 'low',
          })}
        />

        {/* Popularity score badge (always visible when enabled) */}
        {showPopularityScore && content.popularity !== undefined && (
          <div className="absolute top-2 left-2 bg-black/80 text-white text-xs px-2 py-1 rounded-full shadow-lg z-10">
            {content.popularity.toFixed(1)}
          </div>
        )}

        {/* Overlay with content info */}
        <div 
          className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          style={enableOptimizations ? { willChange: 'opacity' } : undefined}
        >
          <div className="absolute bottom-0 left-0 right-0 p-3">
            <div className="flex items-end justify-between">
              {/* Content info on the left */}
              <div className="flex-1 min-w-0 mr-3">
                <h3 className="text-white font-semibold text-sm mb-1 line-clamp-2">
                  {content.title}
                </h3>
                <div className="flex items-center justify-between text-xs text-gray-300">
                  <div className="flex items-center gap-2">
                    <span>{content.year}</span>
                    {content.contentRating && (
                      <span className="px-1.5 py-0.5 bg-gray-700 text-white text-xs font-semibold rounded">
                        {content.contentRating}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {content.rating && (
                      <span className="flex items-center">
                        <svg className="w-3 h-3 text-yellow-400 mr-1" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                        {content.rating.toFixed(1)}
                      </span>
                    )}
                    {showPopularityScore && content.popularity !== undefined && (
                      <span className="flex items-center text-orange-400">
                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                          <path fillRule="evenodd" d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z" clipRule="evenodd" />
                        </svg>
                        {content.popularity.toFixed(1)}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {content.genres.slice(0, 2).map((genre) => (
                    <span
                      key={genre}
                      className="px-2 py-1 bg-gray-700/80 text-white text-xs rounded-full"
                    >
                      {genre}
                    </span>
                  ))}
                </div>
              </div>

              {/* People photos on the right */}
              {displayPeople && displayPeople.length > 0 && (
                <div className="flex-shrink-0">
                  <PeoplePhotosOverlay
                    people={displayPeople}
                    maxVisible={3}
                    onPersonClick={onPersonClick}
                    size="small"
                    className="justify-end"
                    enableOptimizations={enableOptimizations}
                    context="overlay"
                  />
                </div>
              )}
            </div>


          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentCard;